import { create } from 'zustand';
import { QuestionDraft, GroupDraft, uploadFile, extractQuestions, saveQuestions, extractQuestionsMarkdown, extractQuestionsMarkdownBatch } from '../api/qb';
import { createExamPaper } from '../api/exam';

// 年级别名映射：将用户输入的常见别名转换为后端标准枚举值
const GRADE_ALIAS_MAP: Record<string, string> = {
  // 数字格式 → 中文
  '1年级': '一年级', '2年级': '二年级', '3年级': '三年级', '4年级': '四年级',
  '5年级': '五年级', '6年级': '六年级', '7年级': '初一', '8年级': '初二', '9年级': '初三',
  '七年级': '初一', '八年级': '初二', '九年级': '初三',
  // 英文格式（如果有的话）
  'Grade 1': '一年级', 'Grade 2': '二年级', 'Grade 3': '三年级', 'Grade 4': '四年级',
  'Grade 5': '五年级', 'Grade 6': '六年级', 'Grade 7': '初一', 'Grade 8': '初二', 'Grade 9': '初三',
};

/**
 * 将用户输入的年级值转换为后端标准枚举值
 * @param grade 用户输入的年级值
 * @returns 转换后的标准年级值，如果不在映射表中则返回原值
 */
function normalizeGrade(grade?: string): string | undefined {
  if (!grade) return undefined;
  const trimmed = grade.trim();
  // 先检查是否已经是标准值
  if (GRADE_ALIAS_MAP[trimmed]) {
    return GRADE_ALIAS_MAP[trimmed];
  }
  // 否则尝试匹配部分匹配（如 "八年级上册" → "八年级"）
  for (const [alias, standard] of Object.entries(GRADE_ALIAS_MAP)) {
    if (trimmed.includes(alias)) {
      return standard;
    }
  }
  // 都不匹配，返回原值（让后端验证报错）
  return trimmed;
}

// Simple ID generator
const generateId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
};

interface QBState {
  files: File[];
  uploadedFileIds: string[];
  answerFiles: File[];
  uploadedAnswerFileIds: string[];
  draftQuestions: QuestionDraft[];
  draftGroups: GroupDraft[];
  isUploading: boolean;
  isExtracting: boolean;
  isSaving: boolean;
  error: string | null;
  extractionMethod: 'standard' | 'markdown';
  
  // Common Metadata for the current batch
  commonMetadata: {
    name?: string;
    subject?: string;
    stage?: string;
    grade?: string;
    source_type?: string;
    source_name?: string;
    year?: string;
    region?: string;
    exam_type?: string;
  };
  
  // Actions
  addFile: (file: File) => void;
  removeFile: (index: number) => void;
  addAnswerFile: (file: File) => void;
  removeAnswerFile: (index: number) => void;
  uploadAndExtract: () => Promise<void>;
  updateDraft: (index: number, draft: QuestionDraft) => void;
  insertDraft: (index: number, draft: QuestionDraft) => void;
  removeDraft: (index: number) => void;
  updateCommonMetadata: (meta: Partial<QBState['commonMetadata']>) => void;
  setExtractionMethod: (method: 'standard' | 'markdown') => void;
  saveAll: () => Promise<void>;
  clear: () => void;
}

export const useQBStore = create<QBState>((set, get) => ({
  files: [],
  uploadedFileIds: [],
  answerFiles: [],
  uploadedAnswerFileIds: [],
  draftQuestions: [],
  draftGroups: [],
  isUploading: false,
  isExtracting: false,
  isSaving: false,
  error: null,
  extractionMethod: 'markdown',
  commonMetadata: {},

  addFile: (file) => set((state) => ({ files: [...state.files, file], error: null })),
  addAnswerFile: (file) => set((state) => ({ answerFiles: [...state.answerFiles, file], error: null })),
  
  updateCommonMetadata: (meta) => set((state) => ({
    commonMetadata: { ...state.commonMetadata, ...meta }
  })),

  setExtractionMethod: (method) => set({ extractionMethod: method }),

  removeFile: (index) => set((state) => {
    const newFiles = [...state.files];
    newFiles.splice(index, 1);
    return { files: newFiles };
  }),

  removeAnswerFile: (index) => set((state) => {
    const newFiles = [...state.answerFiles];
    newFiles.splice(index, 1);
    return { answerFiles: newFiles };
  }),

  uploadAndExtract: async () => {
    const { files, answerFiles } = get();
    if (files.length === 0) return;

    set({ isUploading: true, error: null });
    
    try {
      // 1. Upload question files in parallel
      const uploadPromises = files.map((file, idx) => {
        return (async () => {
          await new Promise(resolve => setTimeout(resolve, idx * 50));
          return uploadFile(file);
        })();
      });
      const uploadResults = await Promise.allSettled(uploadPromises);
      
      const uploadedIds: string[] = [];
      const failedUploads: string[] = [];

      uploadResults.forEach((res, index) => {
        if (res.status === 'fulfilled') {
          uploadedIds.push(res.value.file_id);
        } else {
          failedUploads.push(files[index].name);
          console.error(`Upload failed for ${files[index].name}:`, res.reason);
        }
      });

      // 2. Upload answer files in parallel (if any)
      const uploadedAnswerIds: string[] = [];
      if (answerFiles.length > 0) {
        const answerUploadPromises = answerFiles.map((file, idx) => {
          return (async () => {
            await new Promise(resolve => setTimeout(resolve, idx * 50));
            return uploadFile(file);
          })();
        });
        const answerUploadResults = await Promise.allSettled(answerUploadPromises);
        
        answerUploadResults.forEach((res, index) => {
          if (res.status === 'fulfilled') {
            uploadedAnswerIds.push(res.value.file_id);
          } else {
            failedUploads.push(answerFiles[index].name);
            console.error(`Answer upload failed for ${answerFiles[index].name}:`, res.reason);
          }
        });
      }

      if (uploadedIds.length === 0) {
        set({ 
          isUploading: false, 
          error: `All uploads failed: ${failedUploads.join(', ')}` 
        });
        return;
      }

      set({ 
        uploadedFileIds: uploadedIds, 
        uploadedAnswerFileIds: uploadedAnswerIds,
        isUploading: false, 
        isExtracting: true 
      });
      
      let allQuestions: QuestionDraft[] = [];
      let allGroups: GroupDraft[] = [];
      let detectedMetadata = {};
      const extractionErrors: string[] = [];
      const { extractionMethod } = get();

      if (extractionMethod === 'markdown') {
        try {
          console.log(`Starting markdown batch extraction for ${uploadedIds.length} files (Answers: ${uploadedAnswerIds.length})`);
          const result = await extractQuestionsMarkdownBatch(uploadedIds, uploadedAnswerIds);
          if (result.metadata) {
            detectedMetadata = { ...detectedMetadata, ...result.metadata };
          }
          if (result.paperMetadata) {
            detectedMetadata = { ...detectedMetadata, ...result.paperMetadata };
          }
          const questionsWithId = (result.questions || []).map(q => ({ ...q, id: generateId() }));
          allQuestions = [...allQuestions, ...questionsWithId];
          if (result.groups && result.groups.length > 0) {
            allGroups = [...allGroups, ...result.groups];
          }
        } catch (err: any) {
          console.error(`Markdown batch extraction failed:`, err);
          extractionErrors.push(`批量识别失败: ${err.message || 'Unknown error'}`);
        }
      } else {
        for (let i = 0; i < uploadedIds.length; i++) {
          const id = uploadedIds[i];
          console.log(`Starting extraction for fileId ${id} (index ${i})`);
          try {
            const result = await extractQuestions(id);
            console.log(`Extraction success for fileId ${id} using ${extractionMethod}`);

            if (result.metadata) {
              detectedMetadata = { ...detectedMetadata, ...result.metadata };
            }
            if (result.paperMetadata) {
              detectedMetadata = { ...detectedMetadata, ...result.paperMetadata };
            }
            const questionsWithId = (result.questions || []).map(q => ({ ...q, id: generateId() }));
            allQuestions = [...allQuestions, ...questionsWithId];
            if (result.groups && result.groups.length > 0) {
              allGroups = [...allGroups, ...result.groups];
            }
          } catch (err: any) {
            console.error(`Extraction failed for fileId ${id}:`, err);
            extractionErrors.push(`第 ${i + 1} 个文件提取失败: ${err.message || 'Unknown error'}`);
          }
        }
      }
      
      // Construct error message if needed
      let finalError = null;
      if (failedUploads.length > 0 || extractionErrors.length > 0) {
        const parts = [];
        if (failedUploads.length > 0) parts.push(`上传失败: ${failedUploads.join(', ')}`);
        if (extractionErrors.length > 0) parts.push(`识别失败: ${extractionErrors.join(', ')}`);
        finalError = parts.join('; ');
      }
      
      // Update store with questions, groups, AND detected metadata
      set((state) => ({
          draftQuestions: allQuestions,
          draftGroups: allGroups,
          isExtracting: false,
          commonMetadata: { ...state.commonMetadata, ...detectedMetadata },
          error: finalError
      }));
      
      if (allQuestions.length === 0 && finalError) {
        // If nothing extracted and there was an error, ensure UI shows error
        // (already set above)
      }

    } catch (e: any) {
      set({ error: e.message || 'Operation failed', isUploading: false, isExtracting: false });
    }
  },

  updateDraft: (index, draft) => set((state) => {
    const newDrafts = [...state.draftQuestions];
    newDrafts[index] = draft;
    return { draftQuestions: newDrafts };
  }),

  insertDraft: (index, draft) => set((state) => {
    const newDrafts = [...state.draftQuestions];
    newDrafts.splice(index, 0, draft);
    return { draftQuestions: newDrafts };
  }),

  removeDraft: (index) => set((state) => {
    const newDrafts = [...state.draftQuestions];
    newDrafts.splice(index, 1);
    return { draftQuestions: newDrafts };
  }),

  saveAll: async () => {
    const { draftQuestions, draftGroups, commonMetadata } = get();
    if (draftQuestions.length === 0 && draftGroups.length === 0) return;

    set({ isSaving: true, error: null });
    try {
      // 1. Create exam paper with paper-level metadata
      const paper = await createExamPaper({
        name: commonMetadata.name || undefined,
        subject: commonMetadata.subject || undefined,
        stage: commonMetadata.stage || undefined,
        grade: normalizeGrade(commonMetadata.grade) || undefined,
        year: commonMetadata.year ? Number(commonMetadata.year) : undefined,
        exam_type: commonMetadata.exam_type || undefined,
        tags: {
          source_type: commonMetadata.source_type || null,
          source_name: commonMetadata.source_name || null,
        },
      });

      // 2. Merge question-level metadata only (no year/region/exam_type)
      const questionsToSave = draftQuestions.map(q => ({
        ...q,
        subject: q.subject || commonMetadata.subject,
        stage: q.stage || commonMetadata.stage,
        grade: normalizeGrade(q.grade || commonMetadata.grade),
      }));

      const groupsToSave = draftGroups.map(g => ({
        ...g,
        questions: g.questions.map(sq => ({
          ...sq,
          subject: sq.subject || commonMetadata.subject,
          stage: sq.stage || commonMetadata.stage,
          grade: sq.grade || commonMetadata.grade,
        })),
        subject: g.subject || commonMetadata.subject,
        stage: g.stage || commonMetadata.stage,
        grade: g.grade || commonMetadata.grade,
      }));

      // 3. Save questions linked to the paper
      const result = await saveQuestions(questionsToSave, groupsToSave.length > 0 ? groupsToSave : undefined, paper.id);

      if (result.failed_count > 0) {
           console.error("Save errors:", result.errors);

           const failedIndices = new Set(result.errors.map(e => e.index));
           const remainingDrafts = draftQuestions.filter((_, idx) => failedIndices.has(idx));

           set({
               isSaving: false,
               draftQuestions: remainingDrafts,
               draftGroups: [],
               files: remainingDrafts.length === 0 ? [] : get().files,
               uploadedFileIds: remainingDrafts.length === 0 ? [] : get().uploadedFileIds,
               commonMetadata: remainingDrafts.length === 0 ? {} : get().commonMetadata,
               error: result.failed_count > 0 ? `部分题目保存失败 (${result.failed_count} 个)` : null
           });

           alert(`保存完成: ${result.saved_count} 成功, ${result.failed_count} 失败。\n失败题目已保留在列表中。`);
       } else {
           set({ isSaving: false, draftQuestions: [], draftGroups: [], files: [], uploadedFileIds: [], commonMetadata: {}, error: null });
           alert('全部保存成功！');
       }
     } catch (e: any) {
      set({ error: e.message || 'Save failed', isSaving: false });
    }
  },

  clear: () => set({
    files: [],
    uploadedFileIds: [],
    answerFiles: [],
    uploadedAnswerFileIds: [],
    draftQuestions: [],
    draftGroups: [],
    error: null
  })
}));
